import React, {
  useCallback,
  useMemo,
  useState,
  FC,
  PropsWithChildren,
} from 'react';

import { useAlloyFeatureFlag, useCurrentProjectId } from '@adsk/alloy-provider';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';

import { getSpecs, getCustomAttributes } from '../api/entities';
import { SUBMITTALS_CUSTOM_ATTRIBUTES_FF } from '../consts';
import {
  SpecRawData,
  CustomAttributesData,
  CustomAttributesResponse,
} from '../types';

interface SubmittalsContextData {
  specs: {
    data: SpecRawData[];
    isLoading: boolean;
    error?: Error;
    fetchSpecs: () => void;
  };
  customAttributes: {
    data: CustomAttributesData[];
    isLoading: boolean;
    error?: Error;
    fetchCustomAttributes: () => void;
  };
}

export const SubmittalsContext =
  getOrCreateGlobalContext<SubmittalsContextData>('SubmittalsContext');

const SubmittalsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [specs, setSpecs] = useState<SpecRawData[] | null>(null);
  const [customAttributes, setCustomAttributes] = useState<
    CustomAttributesData[] | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState(undefined);
  const projectId = useCurrentProjectId();
  const { value: isCustomReviewEnabled } = useAlloyFeatureFlag(
    'submittals-crw-fe',
    true,
  );

  const { value: isCustomAttributesEnabled } = useAlloyFeatureFlag(
    SUBMITTALS_CUSTOM_ATTRIBUTES_FF,
    true,
  );

  const fetchSpecs = useCallback(() => {
    if (!projectId || specs !== null) {
      return;
    }

    setIsLoading(true);

    getSpecs(projectId, isCustomReviewEnabled)
      .then((specs) => {
        setSpecs(specs);
        setIsLoading(false);
      })
      .catch((e) => {
        setError(e);
        setIsLoading(false);
      });
  }, [projectId, specs, isCustomReviewEnabled]);

  const fetchCustomAttributes = useCallback(() => {
    if (!isCustomAttributesEnabled || !projectId || customAttributes !== null) {
      return;
    }
    setIsLoading(true);
    getCustomAttributes(projectId)
      .then((customAttributesResponse: CustomAttributesResponse) => {
        setCustomAttributes(customAttributesResponse.results);
        setIsLoading(false);
      })
      .catch((e) => {
        setError(e);
        setIsLoading(false);
      });
  }, [projectId, customAttributes, isCustomAttributesEnabled]);

  const value = useMemo(
    () => ({
      specs: {
        data: specs || [],
        isLoading,
        error,
        fetchSpecs,
      },
      customAttributes: {
        data: isCustomAttributesEnabled ? customAttributes || [] : [],
        isLoading: isCustomAttributesEnabled ? isLoading : false,
        error: isCustomAttributesEnabled ? error : undefined,
        fetchCustomAttributes: isCustomAttributesEnabled
          ? fetchCustomAttributes
          : () => {},
      },
    }),
    [
      specs,
      isLoading,
      error,
      fetchSpecs,
      customAttributes,
      fetchCustomAttributes,
      isCustomAttributesEnabled,
    ],
  );

  return (
    <SubmittalsContext.Provider value={value}>
      {children}
    </SubmittalsContext.Provider>
  );
};

SubmittalsProvider.displayName = 'SubmittalsProvider';

export default SubmittalsProvider;
